import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Table, Alert, Modal } from 'react-bootstrap';
import axios from 'axios';

const ProductOverviewSale = () => {
  const [productOverviewSales, setProductOverviewSales] = useState([]);
  const [selectedSaleId, setSelectedSaleId] = useState(null); // To track the sale being edited
  const [isEditing, setIsEditing] = useState(false); // To toggle between adding and editing mode
  const [products, setProducts] = useState([]); // For product dropdown
  const [newSale, setNewSale] = useState({
    p_id: '',
    title: '',
    desc: '',
    price: '',
    est_price: '',
    price_tag: '',
    beds: '',
    bath: '',
    sq_ft: '',
    about_section_title: '',
    description: ''
  });
  const [alertMessage, setAlertMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false); // To control the modal visibility

  useEffect(() => {
    // Fetch existing product overview sales and products on component mount
    fetchProductOverviewSales();
    fetchProducts();
  }, []);

  // Fetch the product overview sales
  const fetchProductOverviewSales = () => {
    axios.get('https://api.biznetusa.com/api/get-productoverviewsales')
      .then(response => {
        setProductOverviewSales(response.data.product_overview_sales_section);
      })
      .catch(() => {
        setErrorMessage('Error fetching product overview sales.');
      });
  };

  // Fetch products for the dropdown
  const fetchProducts = () => {
    axios.get('https://api.biznetusa.com/api/get-products')
      .then(response => {
        setProducts(response.data.products); // Assuming response contains products array
      })
      .catch(() => {
        setErrorMessage('Error fetching products.');
      });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSale({
      ...newSale,
      [name]: value
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this product overview sale?')) {
      axios.delete(`https://api.biznetusa.com/api/deleteproductoverviewsale/${id}`)
        .then(() => {
          setAlertMessage('Product overview sale deleted successfully!');
          fetchProductOverviewSales(); // Refresh the list after deletion
        })
        .catch(() => {
          setErrorMessage('Error deleting product overview sale.');
        });
    }
  };

  // Handle form submission to add a new product overview sale
 // Modify handleSubmit function to handle both add and update
const handleSubmit = (e) => {
    e.preventDefault();
  
    if (isEditing && selectedSaleId) {
      // Send PUT request to update the product overview sale
      axios.put(`https://api.biznetusa.com/api/update-productoverviewsale/${selectedSaleId}`, newSale)
        .then(() => {
          setAlertMessage('Product overview sale updated successfully!');
          setErrorMessage('');
          setIsEditing(false); // Reset edit mode
          setSelectedSaleId(null); // Reset the selected ID
          setNewSale({
            p_id: '',
            title: '',
            desc: '',
            price: '',
            est_price: '',
            price_tag: '',
            beds: '',
            bath: '',
            sq_ft: '',
            about_section_title: '',
            description: ''
          });
          fetchProductOverviewSales(); // Refresh the list after update
          setShowModal(false); // Close the modal after submission
        })
        .catch(() => {
          setErrorMessage('Error updating product overview sale.');
        });
    } else {
      // Handle new product overview sale addition
      axios.post('https://api.biznetusa.com/api/store-productoverviewsale', newSale)
        .then(() => {
          setAlertMessage('Product overview sale added successfully!');
          setErrorMessage('');
          setNewSale({
            p_id: '',
            title: '',
            desc: '',
            price: '',
            est_price: '',
            price_tag: '',
            beds: '',
            bath: '',
            sq_ft: '',
            about_section_title: '',
            description: ''
          });
          fetchProductOverviewSales(); // Refresh the list after adding
          setShowModal(false); // Close the modal after submission
        })
        .catch(() => {
          setErrorMessage('Error adding product overview sale.');
        });
    }
  };
  // Open the modal
  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleEdit = (sale) => {
    setNewSale({
      p_id: sale.p_id,
      title: sale.title,
      desc: sale.desc,
      price: sale.price,
      est_price: sale.est_price,
      price_tag: sale.price_tag,
      beds: sale.beds,
      bath: sale.bath,
      sq_ft: sale.sq_ft,
      about_section_title: sale.about_section_title,
      description: sale.description
    });
    setSelectedSaleId(sale.id); // Set the selected sale ID for updating
    setIsEditing(true); // Set editing mode to true
    setShowModal(true); // Show the modal for editing
  };
 
  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false); // Reset edit mode
    setSelectedSaleId(null); // Clear the selected sale ID
    setNewSale({
      p_id: '',
      title: '',
      desc: '',
      price: '',
      est_price: '',
      price_tag: '',
      beds: '',
      bath: '',
      sq_ft: '',
      about_section_title: '',
      description: ''
    });
  };

  return (
    <Container>
      <Row className="my-4">
        <Col>
          <h2>Product Overview Sales</h2>
          {alertMessage && <Alert variant="success">{alertMessage}</Alert>}
          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

          {/* Button to open the modal */}
          <Button variant="primary" onClick={handleShowModal}>
            Add New Product Overview Sale
          </Button>
        </Col>
      </Row>

      <Row className="my-4">
        <Col>
          <h3>Existing Product Overview Sales</h3>
          {productOverviewSales.length > 0 ? (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Product ID</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Price</th>
                  <th>Estimated Price</th>
                  <th>Beds</th>
                  <th>Bath</th>
                  <th>Square Feet</th>
                  <th>About Section Title</th>
                  <th>Actions</th>

                </tr>
              </thead>
              <tbody>
                {productOverviewSales.map((sale) => (
                  <tr key={sale.id}>
                    <td>{sale.p_id}</td>
                    <td>{sale.title}</td>
                    <td>{sale.desc}</td>
                    <td>{sale.price}</td>
                    <td>{sale.est_price}</td>
                    <td>{sale.beds}</td>
                    <td>{sale.bath}</td>
                    <td>{sale.sq_ft}</td>
                    <td>{sale.about_section_title}</td>
                    <td>
                      <Button variant="warning" onClick={() => handleEdit(sale)}>Edit</Button>{' '}
                      <Button variant="danger" onClick={() => handleDelete(sale.id)}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info">No product overview sales found.</Alert>
          )}
        </Col>
      </Row>

      {/* Modal for adding new product overview sale */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Product Overview Sale</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            {/* Product ID Dropdown */}
            <Form.Group>
              <Form.Label>Select Product</Form.Label>
              <Form.Control
                as="select"
                name="p_id"
                value={newSale.p_id}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.title} (ID: {product.id})
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group>
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={newSale.title}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                name="desc"
                value={newSale.desc}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Price</Form.Label>
              <Form.Control
                type="text"
                name="price"
                value={newSale.price}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Estimated Price</Form.Label>
              <Form.Control
                type="text"
                name="est_price"
                value={newSale.est_price}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Price Tag</Form.Label>
              <Form.Control
                type="text"
                name="price_tag"
                value={newSale.price_tag}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Beds</Form.Label>
              <Form.Control
                type="text"
                name="beds"
                value={newSale.beds}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Bath</Form.Label>
              <Form.Control
                type="text"
                name="bath"
                value={newSale.bath}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Square Feet</Form.Label>
              <Form.Control
                type="text"
                name="sq_ft"
                value={newSale.sq_ft}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>About Section Title</Form.Label>
              <Form.Control
                type="text"
                name="about_section_title"
                value={newSale.about_section_title}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>About Section Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={newSale.description}
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Button variant="primary" type="submit">
              Add Product
            </Button>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProductOverviewSale;
